import styled,{keyframes} from "styled-components";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import FactorRow from './FactorRow';
import { useSelector, shallowEqual } from "react-redux";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import Anchor from "app/audit/simple/Anchor";
import { calculateHeatmap,vertical_sorted_names,horizontal_sorted_names,factorTitleSorted,addEQ } from "./calculations";
import ItemRow from "./ItemRow";
import Filters from "../Filters";
import Empty from '../Empty'
import html2canvas from 'html2canvas';
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";
import { validateAndSanitizeOutcomes } from "../utils/outcomesDataIntegrity";

// Toggle INCLUDE_DUMMY_COLUMNS to false or remove DUMMY_COLUMNS once testing is complete.
const INCLUDE_DUMMY_COLUMNS = false;
const DUMMY_COLUMNS = ["Dummy Column A", "Dummy Column B", "Dummy Column C", "Dummy Column D", "Dummy Column E", "Dummy Column F", "Dummy Column G", "Dummy Column H", "Dummy Column I", "Dummy Column J", "Dummy Column K", "Dummy Column L", "Dummy Column M", "Dummy Column N", "Dummy Column O", "Dummy Column P", "Dummy Column Q", "Dummy Column R", "Dummy Column S", "Dummy Column T", "Dummy Column U", "Dummy Column V"];
const COLUMN_SCROLL_WIDTH = 150;


const Heatmap = ({ outcomeQ: outcomeQProp, outcomeStandards: outcomeStandardsProp }) =>{
  // Redirect protection for custom data from NewAnalytics
  const { hasCustomData } = useCustomDataRedirect();

  const heatmapRef = React.useRef();
  const containerRef = React.useRef();
  const scrollContainerRef = React.useRef(null);

  const [expandedRows, setExpandedRows] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Sanitize outcomeQ data to fix duplicate IDs
  const outcomeQ = useMemo(() => {
    if (!outcomeQProp) return null;
    
    const { sanitized, integrity } = validateAndSanitizeOutcomes(outcomeQProp);

    return sanitized;
  }, [outcomeQProp]);
  
  const outcomeStandards = outcomeStandardsProp;
  const [organizationData, setOrganizationData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [verticalTitles, setVerticalTitles] = useState([]);
  const [horizontalTitles, setHorizontalTitles] = useState([]);
  const [factorTitles, setFactorTitles] = useState([]);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(10);
  const [dataFormat, setDataFormat] = useState();
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const combinedRows = useMemo(() => {
    const factors = (factorTitles || []).map((f) => ({
      kind: "factor",
      data: f,
      score: f?.scores ?? -Infinity,
    }));

    const outcomes = (horizontalTitles || [])
      .filter((h) => h?.name !== "Culture")
      .map((h) => ({
        kind: "outcome",
        data: h,
        score: h?.score ?? -Infinity,
      }));

    return [...factors, ...outcomes].sort(
      (a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity)
    );
  }, [factorTitles, horizontalTitles]);

  const core_data = useSelector(
    (state) => state.audit?.core_data,
    shallowEqual
  );

  const get_organizations = useSelector((state) => state.organizations);
  const get_auth = useSelector((state) => state.auth);

  const filtered_data = useSelector(
    (state) => state.audit?.filtered_data,
    shallowEqual
  );

  const responses = useSelector(
    (state) => state.debrief_schedule?.culture_audit_reports,
    shallowEqual
  );

  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );

  const anchor = useSelector((state) => state.audit?.anchor, shallowEqual);

  const reorderHorizontals = useCallback((titles) => {
    if (!Array.isArray(titles)) {
      return titles;
    }
    const cultureIndex = titles.findIndex((title) => title.name === "Culture");
    if (cultureIndex <= 0) {
      return titles;
    }
    const reordered = [...titles];
    const [cultureEntry] = reordered.splice(cultureIndex, 1);
    return [cultureEntry, ...reordered];
  }, []);

  const groupOutcomes = useCallback((titles, outcomeNames) => {
    if (!Array.isArray(titles)) {
      return titles;
    }

    if (!Array.isArray(outcomeNames) || outcomeNames.length === 0) {
      return titles;
    }

    const outcomeItems = titles.filter((title) =>
      outcomeNames.includes(title?.name)
    );
    const remaining = titles.filter(
      (title) => !outcomeNames.includes(title?.name)
    );

    // Merge outcomes directly into the list instead of nesting under a parent
    return [...outcomeItems, ...remaining];
  }, []);

  const handleOutcomeSelect = (selectedOutcomesArray) => {
    // Update selected outcomes directly since Filters component provides the full array
    setSelectedOutcomes(selectedOutcomesArray);
  };

  useEffect(() => {
    if (filtered_data && filtered_data.length > 0) {
      if (Array.isArray(filtered_data[0])) {
        setDataFormat(filtered_data);
      } else {
        setDataFormat([filtered_data]);
      }
    } else if (filtered_data) {
      // Handle case where filtered_data exists but is empty or has different structure
      setDataFormat([filtered_data]);
    } else {
      setDataFormat([]);
    }
  }, [filtered_data]);

  useEffect(() => {
    if (core_data && dataFormat && anchor) {
      // const modified_data = addEQ(filtered_data)
      let rule = core_data?.Whitelabel?.rule
        ? parseInt(core_data?.Whitelabel?.rule)
        : 2;

      // Check if we need to combine surveys with different name_uids
      const isPrimaryAnchor = core_data?.categories[0]?.categories?.find(
        (cat) => cat.id === anchor
      )?.priority === "primary";

      const hasNonPrimaryFilters = selectedFilters?.some((filter) => {
        if (filter.isSubfactor) return false;
        const category = core_data?.categories[0]?.categories?.find((cat) =>
          cat.options?.some((option) => option.id === filter.id)
        );
        return category && category.priority !== "primary";
      });

      let dataToUse = dataFormat;

      // Determine if we need to combine surveys with different name_uids
      // This is only necessary when:
      // 1. Using primary anchor (Overview)
      // 2. Has non-primary filters applied
      // 3. Data structure indicates multiple surveys need combining (responses.response has multiple surveys)
      // 4. dataFormat is not already properly filtered

      const needsToCombineSurveys = isPrimaryAnchor &&
        hasNonPrimaryFilters &&
        responses?.response?.length > 1 &&
        responses.response[0]?.responses; // Check if it has the expected structure

      if (needsToCombineSurveys) {
        // Find the latest date across all surveys
        const allSurveys = responses.response;
        const latestDate = Math.max(...allSurveys.map(survey => new Date(survey.date || survey.scheduled_for).getTime()));

        // Get all surveys that match the latest date (combining different name_uids)
        const latestSurveys = allSurveys.filter(survey => {
          const surveyDate = new Date(survey.date || survey.scheduled_for).getTime();
          return surveyDate === latestDate;
        });

        // Only proceed with combining if we found multiple surveys at the latest date
        if (latestSurveys.length > 1) {
          // Combine all responses from the latest surveys
          const combinedResponses = latestSurveys.flatMap(survey =>
            survey.responses || []
          ).map(response => response.response).filter(r => r && !Array.isArray(r));

          if (combinedResponses.length > 0) {
            dataToUse = [combinedResponses];
          }
        }
      }

      // Check if we're filtering by factors only (no outcomes should be calculated)
      const hasFactorFilters = selectedFilters?.some(
        (filter) => filter.type_of === "factor"
      );
      // Outcomes should be included unless we're filtering by factors only (and no outcomes are selected)
      // Category filters should NOT exclude outcomes - only factor filters should
      const shouldIncludeOutcomes = !hasFactorFilters || selectedOutcomes.length > 0;

      let data = calculateHeatmap(
        core_data,
        dataToUse,
        anchor,
        shouldIncludeOutcomes ? outcomeQ : null,
        shouldIncludeOutcomes ? outcomeStandards : null,
        selectedOutcomes,
        selectedFilters?.length === 0 && selectedOutcomes?.length > 0
      ).map((f) => ({
        ...f,
        meetsThreshold: f.responses > rule
      }));

      setTableData(data);

      // Get selected factors for filtering
      const selectedFactorFilters =
        selectedFilters?.filter((filter) => filter.type_of === "factor") || [];

      const baseVerticalTitles = vertical_sorted_names(data);
      const extraVerticalTitles = INCLUDE_DUMMY_COLUMNS ? DUMMY_COLUMNS : [];
      setVerticalTitles([...baseVerticalTitles, ...extraVerticalTitles]);
      const sortedHorizontalTitles = horizontal_sorted_names(
        data,
        shouldIncludeOutcomes
      );
      const outcomeNames = outcomeQ?.questions?.map((question) => question.name);;

      const reorderedHorizontals = reorderHorizontals(sortedHorizontalTitles);
      setHorizontalTitles(groupOutcomes(reorderedHorizontals, outcomeNames));
      setFactorTitles(factorTitleSorted(data, selectedFactorFilters));

      let min_score = Infinity;
      let max_score = -Infinity;
      let hasValidScores = false;

      // Calculate min/max based on what's actually being displayed
      const horizontalNames = sortedHorizontalTitles;

      data.forEach((item) => {
        // Always include culture scores for overall column if available
        if (
          item.culture_scores?.overall_scores != null &&
          !isNaN(item.culture_scores.overall_scores)
        ) {
          const score = item.culture_scores.overall_scores;
          hasValidScores = true;
          if (score < min_score) {
            min_score = score;
          }
          if (score > max_score) {
            max_score = score;
          }
        }

        // Include factor scores if we have selected factors
        if (selectedFactorFilters.length > 0) {
          Object.values(item.culture_scores.factor_scores || {}).forEach(
            (factorData) => {
              if (
                selectedFactorFilters.some((f) => f.name === factorData.factor)
              ) {
                let score = factorData.scores;
                if (score !== null && score !== undefined && !isNaN(score)) {
                  hasValidScores = true;
                  if (score < min_score) {
                    min_score = score;
                  }
                  if (score > max_score) {
                    max_score = score;
                  }
                }
              }
            }
          );
        }

        // Only include outcome scores if they should be included
        if (shouldIncludeOutcomes) {
          horizontalNames.forEach((horizontal) => {
            if (
              horizontal.name !== "Culture" &&
              horizontal.name !== "Emotional Intelligence" &&
              horizontal.name !== "Emotional Intelligence 360"
            ) {
              let score = item.outcome_scores.overall_scores[horizontal.name];
              if (score !== null && score !== undefined && !isNaN(score)) {
                hasValidScores = true;
                if (score < min_score) {
                  min_score = score;
                }
                if (score > max_score) {
                  max_score = score;
                }
              }
            }
          });
        }

        // Include EQ scores if they're in horizontal titles
        if (
          item.EQ_scores &&
          horizontalNames.some((h) => h.name === "Emotional Intelligence")
        ) {
          Object.keys(item.EQ_scores).forEach((key) => {
            let score = item.EQ_scores[key];
            if (score !== null && score !== undefined && !isNaN(score)) {
              hasValidScores = true;
              if (score < min_score) {
                min_score = score;
              }
              if (score > max_score) {
                max_score = score;
              }
            }
          });
        }

        // Include EQ360 scores if they're in horizontal titles
        if (
          item.EQ360_scores &&
          horizontalNames.some((h) => h.name === "Emotional Intelligence 360")
        ) {
          Object.keys(item.EQ360_scores).forEach((key) => {
            let score = item.EQ360_scores[key];
            if (score !== null && score !== undefined && !isNaN(score)) {
              hasValidScores = true;
              if (score < min_score) {
                min_score = score;
              }
              if (score > max_score) {
                max_score = score;
              }
            }
          });
        }
      });

      // Set reasonable defaults if no valid scores found
      if (!hasValidScores) {
        min_score = 0;
        max_score = 10;
      }

      setMin(min_score);
      setMax(max_score);
    }
  }, [
    core_data,
    dataFormat,
    anchor,
    outcomeQ,
    outcomeStandards,
    selectedFilters,
    selectedOutcomes,
    responses,
    reorderHorizontals,
    groupOutcomes,
  ]);

    useEffect(() => {
      if (get_organizations) {
        setOrganizationData(
          get_organizations[
            get_auth?.organization_id || get_organizations?.organization?.id
          ]
        );
      }
    }, [get_organizations]);

    



  // Debug: Log when outcomes are available
  useEffect(() => {
    if (outcomeQProp?.questions) {
      console.log(
        "[Heatmap] Original Outcomes received:",
        outcomeQProp.questions.map(q => ({ id: q.id, name: q.name }))
      );
    }
    
    if (outcomeQ?.questions) {
      console.log(
        "[Heatmap] Sanitized Outcomes being used:",
        outcomeQ.questions.map(q => ({ id: q.id, name: q.name }))
      );
    }
  }, [outcomeQProp, outcomeQ]);

  useEffect(() => {
    // Keep rows collapsed by default; user can expand via caret
    setExpandedRows([]);
  }, [horizontalTitles]);

  const toggleRow = (rowId) => {
    setExpandedRows((prevExpanded) => {
      if (prevExpanded.includes(rowId)) {
        return prevExpanded.filter((existingId) => existingId !== rowId);
      }
      return [...prevExpanded, rowId];
    });
  };

  const findByOptionName = (name) => {
    return tableData.find((item) => item.option === name);
  };

  // Portal-based Tooltip Component
  const TooltipWrapper = ({ tooltipText }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const wrapperRef = useRef(null);

    const updatePosition = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + window.scrollY - 2,
          left: rect.left + rect.width / 2 + window.scrollX,
        });
      }
    };

    const handleMouseEnter = () => {
      requestAnimationFrame(() => {
        updatePosition();
        setIsHovered(true);
      });
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    useEffect(() => {
      if (isHovered) {
        const handleScroll = () => updatePosition();
        const handleResize = () => updatePosition();
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('scroll', handleScroll, true);
          window.removeEventListener('resize', handleResize);
        };
      }
    }, [isHovered]);

    return (
      <>
        <InfoWrapper
          ref={wrapperRef}
          tabIndex="0"
          aria-label="Anonymity threshold info"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleMouseEnter}
          onBlur={handleMouseLeave}
        >
          <InfoIcon aria-hidden="true">i</InfoIcon>
        </InfoWrapper>
        {isHovered &&
          ReactDOM.createPortal(
            <TooltipPortal
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
              }}
              role="tooltip"
            >
              {tooltipText}
            </TooltipPortal>,
            document.body
          )}
      </>
    );
  };

  const findEQScore = (title, item) => {
    if (title.name == "Emotional Intelligence") {
      const rawScore = tableData.find((i) => i.option === item)?.EQ_scores
        .Overall;
      return rawScore !== null && rawScore !== undefined
        ? rawScore.toFixed(1)
        : null;
    }

    if (title.name == "Emotional Intelligence 360") {
      const rawScore = tableData.find((i) => i.option === item)?.EQ360_scores
        .Overall;
      return rawScore !== null && rawScore !== undefined
        ? rawScore.toFixed(1)
        : null;
    }
    return null;
  };

  const getScores = (title, item) => {
    const rawScore =
      findByOptionName(item)?.outcome_scores.overall_scores[title.name];
    if (rawScore !== null && rawScore !== undefined && !isNaN(rawScore)) {
      return rawScore.toFixed(1);
    }
    return null;
  };

  const formatScoreDisplay = (score) => {
    if (
      score === null ||
      score === undefined ||
      isNaN(score) ||
      score === "NaN" ||
      score === ""
    ) {
      return "-";
    }
    return score;
  };

  const getColorGrade = (score) => {
    // Handle null, undefined, NaN, or empty string as "no data"
    if (
      score === null ||
      score === undefined ||
      isNaN(score) ||
      score === "" ||
      score === "NaN"
    ) {
      return "#fff";
    }

    // Convert score to number if it's a string
    const numericScore = typeof score === "string" ? parseFloat(score) : score;

    // Double check after conversion
    if (isNaN(numericScore)) {
      return "#fff";
    }

    // Discrete color ranges - Red: 0-4.9, Yellow: 5-6.9, Green: 7-10
    if (numericScore >= 0 && numericScore < 5) {
      return "#dd524c"; // Red
    } else if (numericScore >= 5 && numericScore < 7) {
      return "#f5d565"; // Yellow
    } else if (numericScore >= 7 && numericScore <= 10) {
      return "#43946c"; // Green
    }

    return "#fff";
  };

  const getTextColor = (score) => {
    // All text within cells should be white
    // Yellow text should use #6f391a
    if (score >= 5 && score < 7) {
      return "#6f391a";
    }


    return "#fff";
  };

  const getTotalResponses = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return 0;
    }
    return tableData.reduce((sum, item) => {
      const responses = item?.responses || 0;
      return sum + (typeof responses === 'number' ? responses : 0);
    }, 0);
  }, [tableData]);

  // Calculate weighted overall aggregated score (including everything: culture, outcomes, EQ, EQ360)
  // Weighted by number of responses per category
  const calculateAggregatedOverallScore = useCallback((itemData) => {
    if (!itemData) return null;

    const responses = itemData.responses || 0;
    if (responses === 0) return null;

    const scores = [];

    // Include culture score (overall)
    if (itemData.culture_scores?.overall_scores != null && !isNaN(itemData.culture_scores.overall_scores)) {
      scores.push(itemData.culture_scores.overall_scores);
    }

    // Include all outcome scores
    if (itemData.outcome_scores?.overall_scores) {
      Object.values(itemData.outcome_scores.overall_scores).forEach((outcomeScore) => {
        if (outcomeScore != null && !isNaN(outcomeScore)) {
          scores.push(outcomeScore);
        }
      });
    }

    // Include EQ scores if available
    if (itemData.EQ_scores) {
      Object.values(itemData.EQ_scores).forEach((eqScore) => {
        if (eqScore != null && !isNaN(eqScore)) {
          scores.push(eqScore);
        }
      });
    }

    // Include EQ360 scores if available
    if (itemData.EQ360_scores) {
      Object.values(itemData.EQ360_scores).forEach((eq360Score) => {
        if (eq360Score != null && !isNaN(eq360Score)) {
          scores.push(eq360Score);
        }
      });
    }

    if (scores.length === 0) return null;

    // Calculate weighted average: sum of all scores * responses / (number of scores * responses)
    const sum = scores.reduce((acc, score) => acc + score, 0);
    return sum / scores.length;
  }, []);

  // Calculate aggregated overall score for totals column (weighted across all categories by responses)
  const getAggregatedOverallTotal = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return null;
    }

    let totalWeightedSum = 0;
    let totalWeight = 0;

    tableData.forEach((item) => {
      const responses = item?.responses || 0;
      if (responses === 0) return;

      const scores = [];

      // Include culture score
      if (item.culture_scores?.overall_scores != null && !isNaN(item.culture_scores.overall_scores)) {
        scores.push(item.culture_scores.overall_scores);
      }

      // Include all outcome scores
      if (item.outcome_scores?.overall_scores) {
        Object.values(item.outcome_scores.overall_scores).forEach((outcomeScore) => {
          if (outcomeScore != null && !isNaN(outcomeScore)) {
            scores.push(outcomeScore);
          }
        });
      }

      // Include EQ scores
      if (item.EQ_scores) {
        Object.values(item.EQ_scores).forEach((eqScore) => {
          if (eqScore != null && !isNaN(eqScore)) {
            scores.push(eqScore);
          }
        });
      }

      // Include EQ360 scores
      if (item.EQ360_scores) {
        Object.values(item.EQ360_scores).forEach((eq360Score) => {
          if (eq360Score != null && !isNaN(eq360Score)) {
            scores.push(eq360Score);
          }
        });
      }

      if (scores.length > 0) {
        // Average of all scores for this category
        const categoryAvg = scores.reduce((acc, score) => acc + score, 0) / scores.length;
        // Weight by number of responses
        totalWeightedSum += categoryAvg * responses;
        totalWeight += responses;
      }
    });

    return totalWeight > 0 ? totalWeightedSum / totalWeight : null;
  }, [tableData]);

  const renderOverallCell = (summary) => {
    if (summary.children) {
      return "-";
    }
    return formatScoreDisplay(
      summary.score !== null && summary.score !== undefined
        ? summary.score.toFixed(1)
        : null
    );
  };

  // Determine if we should show only Totals column (when anchor is "Overview" and there's only 1 anchor option)
  const shouldShowOnlyTotals = useMemo(() => {
    if (!core_data || !anchor || !verticalTitles || verticalTitles.length === 0) {
      return false;
    }

    const anchorCategory = core_data?.categories?.[0]?.categories?.find(
      (cat) => cat.id === anchor
    );

    // Check if it's the primary anchor (Overview) - use same check as elsewhere in code
    const isPrimaryAnchor = anchorCategory?.priority === "primary";
    const hasOnlyOneOption = verticalTitles.length === 1;

    return isPrimaryAnchor && hasOnlyOneOption;
  }, [core_data, anchor, verticalTitles]);

  const renderDataCells = (summary, keyPrefix) => {
    if (shouldShowOnlyTotals) {
      return null; // Don't render column cells when showing only totals
    }

    if (summary.children) {
      return verticalTitles.map((item) => (
        <TableCell key={`${keyPrefix}-${item}`} color="#fff">
          -
        </TableCell>
      ));
    }

    if (summary.name === "Culture") {
      return verticalTitles.map((item) => {
        const itemData = findByOptionName(item);
        const meetsThreshold = itemData?.meetsThreshold !== false; // Default to true if not set

        if (!meetsThreshold) {
          return (
            <TableCell key={`${keyPrefix}-${item}`} color="#fff" textColor="#000">
              -
            </TableCell>
          );
        }

        let rawScore = itemData?.culture_scores.overall_scores;
        let value =
          rawScore !== null && rawScore !== undefined
            ? rawScore.toFixed(1)
            : null;
        return (
          <TableCell key={`${keyPrefix}-${item}`} color={getColorGrade(value)} textColor={getTextColor(value)}>
            {formatScoreDisplay(value)}
          </TableCell>
        );
      });
    }

    return verticalTitles.map((item) => {
      const itemData = findByOptionName(item);
      const meetsThreshold = itemData?.meetsThreshold !== false; // Default to true if not set

      if (!meetsThreshold) {
        return (
          <TableCell key={`${keyPrefix}-${item}`} color="#fff" textColor="#000">
            -
          </TableCell>
        );
      }

      let value =
        summary.name == "Emotional Intelligence" ||
        summary.name == "Emotional Intelligence 360"
          ? findEQScore(summary, item)
          : getScores(summary, item);
      return (
        <TableCell key={`${keyPrefix}-${item}`} color={getColorGrade(value)} textColor={getTextColor(value)}>
          {formatScoreDisplay(value)}
        </TableCell>
      );
    });
  };

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    const canScroll = maxScrollLeft > 0;
    setCanScrollLeft(canScroll && scrollLeft > 0);
    setCanScrollRight(canScroll && scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleScroll = () => updateScrollState();
    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateScrollState);
    updateScrollState();

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, horizontalTitles, verticalTitles, tableData]);

  const scrollTable = (direction) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const step = COLUMN_SCROLL_WIDTH;
    const current = el.scrollLeft;
    const remainder = current % step;

    let delta = direction * step;

    if (remainder !== 0) {
      delta = direction > 0 ? step - remainder : -remainder;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    const target = Math.max(0, Math.min(current + delta, maxScroll));
    el.scrollTo({ left: target, behavior: "smooth" });
  };

  const downloadHeatmapAsPNG = async () => {
    const el = heatmapRef.current;
    if (!el) return;

    // Suppose containerRef points to the parent <Container>
    const parent = containerRef.current;

    // Save original styles
    const originalOverflow = parent.style.overflow;
    const originalWidth = parent.style.width;
    const originalHeight = parent.style.height;

    try {
      // Temporarily allow full expansion
      parent.style.overflow = "visible";
      parent.style.width = "auto";
      parent.style.height = "auto";

      // 3) Use html2canvas with large windowWidth and windowHeight if needed
      const canvas = await html2canvas(el, {
        scrollX: 0,
        scrollY: 0,
        // Optionally increase scale for higher resolution
        scale: 2,
        x: -50,
        y: -50,
        width: parent.scrollWidth + 100,
        height: el.clientHeight + 100,
      });

      // 4) Convert to PNG data URL and download
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = "heatmap.png";
      link.href = dataUrl;
      link.click();
    } finally {
      // 5) Restore original styles
      parent.style.overflow = originalOverflow;
      parent.style.width = originalWidth;
      parent.style.height = originalHeight;
    }
  };

  if (core_data?.noData && !hasCustomData) {
    return <Empty />;
  }

  const hasHorizontalScroll = canScrollLeft || canScrollRight;

  return (
    <Container ref={containerRef}>
      <AnalyticsBreadcrumb currentComponent="heatmap" />
      <HeaderRow>
        <Title>
          <TitleIconWrapper aria-hidden>
            <HeatmapIcon />
          </TitleIconWrapper>
          Heatmapping
        </Title>
        <Button onClick={downloadHeatmapAsPNG}>Download Heatmap</Button>
      </HeaderRow>
      {/* <Description>
        The Heatmap provides a visual representation of the categories and
        outcome scores for each category tag. The scores are based on aggregate
        scores from the survey. The heatmap is color-coded to show the relative
        scores for each organization, with red indicating a lower score and
        green indicating a higher score.
      </Description> */}

      <T2>
        Please select an anchor to visualize different response scores and patterns.
      </T2>
      <AnchorFilterRow>
        <Anchor />
        <Filters
          outcomes={outcomeQ?.questions}
          onSelectOutcomes={handleOutcomeSelect}
          hideFactors={false}
          filters={true}
        />
      </AnchorFilterRow>

      <Content id="heatmapWrapper" ref={heatmapRef}>
        {getTotalResponses == 0 ? (
          <Empty />
        ) : (
            <>
              <ScoreLegend>
                <LegendLabel>SCORE RANGE:</LegendLabel>
                <LegendItem backgroundColor="#dd524c" textColor="#fff">0-4</LegendItem>
                <LegendItem backgroundColor="#f5d565" textColor="#6f391a">5-6</LegendItem>
                <LegendItem backgroundColor="#43946c" textColor="#fff">7-10</LegendItem>
              </ScoreLegend>
          <ScrollRegion>
            {hasHorizontalScroll && (
              <ScrollButton
                type="button"
                aria-label="Scroll heatmap left"
                onClick={() => scrollTable(-1)}
                disabled={!canScrollLeft}
              >
                ‹
              </ScrollButton>
            )}
            <ScrollX ref={scrollContainerRef}>
              {canScrollRight && <RightFade />}
              <Table>
                    <FirstColumnHeaderOverlay />
                <thead>
                  <tr>
                    <TableHeader></TableHeader>{" "}
                    {/* Empty header for first column */}
                    <TableHeader>
                          <SlantedHeader>
                            Totals
                            <TooltipWrapper
                              tooltipText="Totals shows the total number of respondents, and the overall average scores per category. This is based on the data assigned to your group, and your current filters."
                            />
                          </SlantedHeader>
                </TableHeader>
                        {!shouldShowOnlyTotals && verticalTitles.map((title) => {
                          const item = findByOptionName(title);
                          const meetsThreshold = item?.meetsThreshold !== false; // Default to true if not set
                          const isTruncated = title.length > 16;
                          const displayTitle = isTruncated ? `${title.substring(0, 13)}...` : title;
                          return (
                            <TableHeader key={title}>
                              <SlantedHeader>
                                <span title={isTruncated ? title : undefined}>
                                  {displayTitle}
                                </span>
                                {!meetsThreshold && (
                                  <TooltipWrapper
                                    tooltipText="This tags/category does not meet the anonymity threshold of respondents required to display data."
                                  />
                                )}
                              </SlantedHeader>
                            </TableHeader>
                          );
                        })}
                {/* Add other headers as needed */}
              </tr>
            </thead>
            <tbody>


              <TableRow3>
                <FirstColumnCellResponse>
                  Number of responses
                </FirstColumnCellResponse>
                        <TableCell2>{getTotalResponses}</TableCell2>
                        {!shouldShowOnlyTotals && verticalTitles.map((title) => (
                  <TableCell2 key={title}>
                    {findByOptionName(title)?.responses}
                  </TableCell2>
                ))}
                {/* Add other cells as needed */}
              </TableRow3>
                      {/* Aggregated Overall Score Row */}
                      <TableRow3>
                        <FirstColumnCellResponse>
                          Overall Score
                        </FirstColumnCellResponse>
                        {getAggregatedOverallTotal != null ? (
                          <TableCell
                            color={getColorGrade(getAggregatedOverallTotal.toFixed(1))}
                            textColor={getTextColor(getAggregatedOverallTotal.toFixed(1))}
                          >
                            {formatScoreDisplay(getAggregatedOverallTotal.toFixed(1))}
                          </TableCell>
                        ) : (
                          <TableCell2>-</TableCell2>
                        )}
                        {!shouldShowOnlyTotals && verticalTitles.map((title) => {
                          const itemData = findByOptionName(title);
                          const meetsThreshold = itemData?.meetsThreshold !== false; // Default to true if not set

                          if (!meetsThreshold) {
                            return (
                              <TableCell key={title} color="#fff" textColor="#000">
                                -
                              </TableCell>
                            );
                          }

                          const aggregatedScore = calculateAggregatedOverallScore(itemData);
                          return aggregatedScore != null ? (
                            <TableCell
                              key={title}
                              color={getColorGrade(aggregatedScore.toFixed(1))}
                              textColor={getTextColor(aggregatedScore.toFixed(1))}
                            >
                              {formatScoreDisplay(aggregatedScore.toFixed(1))}
                            </TableCell>
                          ) : (
                            <TableCell2 key={title}>-</TableCell2>
                          );
                        })}
                      </TableRow3>
                      {combinedRows.map((row) => {
                        if (row.kind === "factor") {
                          const factor = row.data;
                          return (
                            <FactorRow
                              key={`factor-${factor.name}`}
                              summary={factor}
                              data={tableData}
                              verticalTitles={shouldShowOnlyTotals ? [] : verticalTitles}
                              findByOptionName={findByOptionName}
                              isExpanded={true}
                              min={min}
                              max={max}
                            />
                          );
                        }

                        const title = row.data;
                const rowId = title.name;
                const isGroup =
                  Array.isArray(title.children) && title.children.length > 0;
                const isExpanded = expandedRows.includes(rowId);
                return (
                  <React.Fragment key={rowId}>
                    <TableRow>
                      <OutcomeFirstColumnCell onClick={() => toggleRow(rowId)}>
                        {title.name}
                        <CaretIcon isExpanded={isExpanded} />
                      </OutcomeFirstColumnCell>
                      <TableCell
                        color={getColorGrade(title.score !== null && title.score !== undefined ? title.score.toFixed(1) : null)}
                        textColor={getTextColor(title.score !== null && title.score !== undefined ? title.score.toFixed(1) : null)}
                      >
                        {renderOverallCell(title)}
                      </TableCell>

                      {renderDataCells(title, rowId)}
                    </TableRow>
                    {isGroup && isExpanded
                      ? title.children.map((child) => {
                          const childRowId = child.name;
                          const childExpanded =
                            expandedRows.includes(childRowId);
                          return (
                            <React.Fragment key={childRowId}>
                              <TableRow>
                                <OutcomeChildFirstColumnCell
                                  onClick={() => toggleRow(childRowId)}
                                >
                                  {child.name}
                                  <CaretIcon isExpanded={childExpanded} />
                                </OutcomeChildFirstColumnCell>
                                <TableCell
                                  color={getColorGrade(child.score !== null && child.score !== undefined ? child.score.toFixed(1) : null)}
                                  textColor={getTextColor(child.score !== null && child.score !== undefined ? child.score.toFixed(1) : null)}
                                >
                                  {renderOverallCell(child)}
                                </TableCell>
                                {renderDataCells(child, childRowId)}
                              </TableRow>
                              <ItemRow
                                key={`${childRowId}-details`}
                                summary={child}
                                data={tableData}
                                verticalTitles={shouldShowOnlyTotals ? [] : verticalTitles}
                                findByOptionName={findByOptionName}
                                isExpanded={childExpanded}
                                min={min}
                                max={max}
                              />
                            </React.Fragment>
                          );
                        })
                      : null}
                    {!isGroup ? (
                      <ItemRow
                        key={title.name}
                        summary={title}
                        data={tableData}
                        verticalTitles={shouldShowOnlyTotals ? [] : verticalTitles}
                        findByOptionName={findByOptionName}
                        isExpanded={isExpanded}
                        min={min}
                        max={max}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
              </Table>
            </ScrollX>
            <FirstColumnCover aria-hidden />
            {hasHorizontalScroll && (
              <ScrollButton
                type="button"
                aria-label="Scroll heatmap right"
                onClick={() => scrollTable(1)}
                disabled={!canScrollRight}
                align="right"
              >
                ›
              </ScrollButton>
            )}
          </ScrollRegion>
            </>
        )}
      </Content>
    </Container>
  );
}

export default Heatmap;

const ScoreLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const LegendLabel = styled.span`
  font-weight: bold;
  font-size: 14px;
  color: #000;
  margin-right: 4px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  background-color: ${props => props.backgroundColor};
  color: ${props => props.textColor};
  font-weight: 500;
  font-size: 14px;
  border-radius: 4px;
`;

const HeatmapIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M3 9H21M3 15H21M12 3V21M7.8 3H16.2C17.8802 3 18.7202 3 19.362 3.32698C19.9265 3.6146 20.3854 4.07354 20.673 4.63803C21 5.27976 21 6.11984 21 7.8V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CaretIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 8px;
  color: #000;
  font-size: 12px;
  
  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    transition: transform 0.2s ease;
    
    ${props => props.isExpanded ? `
      border-width: 6px 5px 0 5px;
      border-color: currentColor transparent transparent transparent;
    ` : `
      border-width: 5px 0 5px 6px;
      border-color: transparent transparent transparent currentColor;
    `}
  }
`;


const Container = styled.div`
    width:100%;
    height:100%;
    display:flex;
    flex-direction:column;
    font-family: 'Raleway';
    overflow-y: visible; 
    position:relative;

    /* Customize scrollbar thumb */
  ::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3); /* Default color when not hovered */
    border-radius: 4px; /* Rounded corners */
  }

  /* Darker scrollbar thumb on hover */
  ::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.6); /* Darker color on hover */
  }

  /* Hide horizontal scrollbar by default */
  ::-webkit-scrollbar {
    height: 0;
  }

  /* Show horizontal scrollbar on hover */
  &:hover::-webkit-scrollbar {
    height: 8px;
  }
`

const HeaderRow = styled.div`
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:16px;
`;

const Title = styled.div`
    font-size:20px;
    font-weight:bold;
    margin-top:20px;
    margin-bottom:15px;
    color:#2d70e2;
    text-transform:uppercase;
    display:flex;
    align-items:center;
    gap:10px;
`

const TitleIconWrapper = styled.span`
    display:inline-flex;
    align-items:center;
    justify-content:center;
    padding:8px;
    background:#F8F9FA;
    border-radius:7px;
    color:#1F2937;
`
const Description = styled.div`
    font-size:14px;
    font-weight:400;
    margin-bottom:30px;
    line-height:1.5;
    color:#2A3039;
    max-width:70%;
`

const T2 = styled.div`
  font-size:14px;
  font-weight:600;
  margin-bottom:10px;
  margin-top:20px;
`

const AnchorFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`

const ScrollRegion = styled.div`
  position:relative;
  width:100%;
`;

const FirstColumnCover = styled.div`
  position:absolute;
  top: -6px;
  left: 0px;
  width:350px;
  height:120px;
  pointer-events:none;
  background:#fff;
  z-index:3;
`;

const FirstColumnHeaderOverlay = styled.div`
  position: fixed;
    top: 342px;
  left: 0;
  width: 350px; /* Match first column width */
  min-width: 350px;
  height: 115px; /* Cover only the slanted label extension area (35px extension, but shorter to not touch data) */
  background-color: #fff;
  z-index: 105; /* Well above thead z-index of 100 */
  pointer-events: none; /* Allow clicks to pass through */
  border-top: none;
  border-bottom: none;
`;

const ScrollX = styled.div`
  position:relative;
  width:100%;
  overflow-x:auto;
  overflow-y:hidden;
  padding-bottom:8px;
  scrollbar-width:none;

  &::-webkit-scrollbar {
    display:none;
  }
`;

const RightFade = styled.div`
  pointer-events:none;
  position:sticky;
  right:0;
  top:0;
  width:32px;
  height:100%;
  background:linear-gradient(to right, rgba(255,255,255,0), #fff);
`;

const ScrollButton = styled.button`
  position:absolute;
  top: 70%;
  transform:translateY(-50%);
  ${props => props.align === 'right' ? 'right:-25px;' : 'left:-25px;'}
  display:flex;
  align-items:center;
  justify-content:center;
  width:32px;
  height:64px;
  border:none;
  border-radius:8px;
  background:#f1f3f9;
  color:#2D70E2;
  font-size:20px;
  font-weight:600;
  cursor:${props => props.disabled ? 'default' : 'pointer'};
  pointer-events:${props => props.disabled ? 'none' : 'auto'};
  opacity:${props => props.disabled ? 0.4 : 1};
  box-shadow:0 4px 12px rgba(0,0,0,0.08);
  transition:opacity 0.2s ease, transform 0.2s ease;
  z-index:6;
`;

const Table = styled.table`
  border-collapse: collapse;
  margin-top: 75px;
  
  thead {
    position: relative;
    z-index: 100;
  }
`;

const TableHeader = styled.th`
  padding: 0;
  height: 40px;
  width: 40px;
  min-width:40px;
  vertical-align: bottom;
  position: relative;
  z-index: 100;
`;

const SlantedHeader = styled.div`
  transform: rotate(-45deg);
  transform-origin: left bottom;
  white-space: nowrap;
  position: absolute;
  bottom: -35px; /* Adjust to align the text properly */
  left: 35px; /* Adjust to align the text properly */
  width: 175px; /* Adjust width based on rotation and content */
  height: 50px;
  min-height:40px;
  line-height: 1;
  border-top: 1px solid #DFDFDF;
  display:flex;
  align-items: center;
  gap: 4px;
  padding-left: 35px;
  padding-bottom:10px;
  color:#000;
  font-weight:500;
  overflow: hidden;           /* Ensures the overflowed text is hidden */
  text-overflow: ellipsis;    /* Adds the ellipsis (...) */
`;

const TableRow = styled.tr`
 height: 35px;
  
`;

const TableRow3 = styled.tr`
 height: 35px;
 background-color: #F8FAFF;
  
`;


const TableRow2 = styled.tr`
    max-height:${props=>props.selected ? '200px' : '0px'};
    height: ${props=>props.selected ? '35px' : '0px'};


    overflow:hidden;
    transition:all 0.3s;
    

 
`;


const TableCell = styled.td`
  border: 1px solid #ddd;
  padding: 0;
  text-align: center;
  width: 50px;
  min-width:50px;
  height:100%;
  font-weight: 500;

  background-color: ${props => props.color || '#fff'};
  color: ${props => props.textColor || '#000'};

`;

const TableCell2 = styled.td`
  border: 1px solid #ddd;
  padding: 0;
  text-align: center;
  width: 50px;
  min-width:50px;
  height:100%;
  font-size:12px;
  font-weight:600;

   background-color: #F8FAFF;

`;

const FirstColumnCell = styled.td`
  border: 1px solid #ddd;
  padding: 0 8px;
  text-align: left;
  width: 350px; /* Wider first column */
  min-width: 350px;
  height: 100%;
  background-color: #fff;
  font-size: 14px;
  cursor: pointer;
  color: #2D70E2;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 2;
`;

const OutcomeChildFirstColumnCell = styled(FirstColumnCell)`
  padding: 0 16px;
  font-weight: 600;
  color: #2D70E2;
`;

// Indent outcome rows to visually separate them similar to categories
const OutcomeFirstColumnCell = styled(FirstColumnCell)`
  padding-left: 16px;
`;

const FirstColumnCellResponse = styled.td`
  border: 1px solid #ddd;
  padding: 0 8px;
  text-align: left;
  width: 350px; /* Wider first column */
  min-width: 350px;
  height: 100%;
  background-color: #F8FAFF;
  text-transform: uppercase;
  font-size: 10px;
  cursor: pointer;
  font-weight: 500;
  position: sticky;
  left: 0;
  z-index: 2;
`;


const Content = styled.div`
    border-radius:10px;
    margin-top:30px;
    display:flex;
    flex-direction:column;
    align-items:flex-start;
    justify-content:center;
    width:100%;
    height:100%;
    position:relative;
    margin-bottom:50px;

    min-height:250px;
`

const Button = styled.button`
    font-family: 'Raleway';
    background-color:#2D70E2;
    color:#fff;
    font-size:14px;
    font-weight:600;
    padding:10px 20px;
    border:none;
    border-radius:5px;
    cursor:pointer;
    white-space:nowrap;
`

const InfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  color: #4b5563;
  background: #fff;
  font-size: 12px;
  cursor: default;
  outline: none;
  flex-shrink: 0;
  margin-left: 4px;
`;

const InfoIcon = styled.span`
  line-height: 1;
  font-weight: 700;
`;

const TooltipPortal = styled.div`
  position: fixed;
  transform: translate(-50%, -100%);
  margin-top: -4px;
  background: #111827;
  color: #f9fafb;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: normal;
  text-align: left;
  max-width: 360px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  z-index: 99999;
  pointer-events: none;
`;

const sampleData = [
    {
      category: 'Culture',
      overall: 75,
      customerService: 3,
      sales: -2,
      marketing: -5,
      operations: -15,
      // Add other fields as needed
    },
    {
      category: 'Innovation',
      overall: 76,
      customerService: 4,
      sales: -3,
      marketing: -6,
      operations: -16,
      // Add other fields as needed
    },
    {
        category: 'Engagement',
        overall: 76,
        customerService: 4,
        sales: -3,
        marketing: -6,
        operations: -16,
        // Add other fields as needed
      },
    {
        category: 'Emotional Intelligence',
        overall: 76,
        customerService: 4,
        sales: -3,
        marketing: -6,
        operations: -16,
        // Add other fields as needed
      },
    // Add more rows as needed
  ];

  
const expanded = [
    {
      category: 'Psychological Safety',
      overall: 75,
      customerService: 3,
      sales: -2,
      marketing: -5,
      operations: -15,
      // Add other fields as needed
    },
    {
      category: 'Clarity',
      overall: 76,
      customerService: 4,
      sales: -3,
      marketing: -6,
      operations: -16,
      // Add other fields as needed
    },
    {
        category: 'Communication',
        overall: 76,
        customerService: 4,
        sales: -3,
        marketing: -6,
        operations: -16,
        // Add other fields as needed
      },
    {
        category: 'Empathy',
        overall: 76,
        customerService: 4,
        sales: -3,
        marketing: -6,
        operations: -16,
        // Add other fields as needed
      },
    // Add more rows as needed
];